
import {VCCSupportSite} from "@/global-scripts/vcc-support-site.js";
import {VCCArticle} from "@/global-scripts/vcc-support-article.js";
import {Component, Vue, Watch} from "vue-property-decorator";
import {TopicModel} from "@/models/TopicModel";
import {getModule} from "vuex-module-decorators";
import ContentModule from "@/store/modules/ContentModule";
import RelatedTopics from "@/components/RelatedTopics/RelatedTopics.vue";
import {CategoryModel} from "@/models/CategoryModel";
import FilterModule from "@/store/modules/FilterModule";
import {Helpers} from "@/global-scripts/helpers";
import AddToPrintButton from "@/components/AddToPrintButton/AddToPrintButton.vue";
import "imagesloaded";
import {VariantFilter} from "@/models/VariantFilter";
import {TopicHelper} from "@/global-scripts/topic-helper";
import {Api} from "@/api/Api";
import ImageZoomModal from "@/components/ImageZoomModal/ImageZoomModal.vue";
import JQuery from "jquery";
import $ from "jquery";

/* eslint-disable-next-line */
import ClickEvent = JQuery.ClickEvent;
import {Route} from "vue-router";
import MemberModule from "@/store/modules/MemberModule";
import PageTracker from "@/utils/PageTracker";
import {ValidityDomains} from "@/settings/ValidityDomains";

const LONG_WORD_CHAR_COUNT = 16;

@Component({
  components: {
    RelatedTopics,
    AddToPrintButton,
    ImageZoomModal
  },
})
export default class Topic extends Vue {
  readonly contentModule = getModule(ContentModule, this.$store);
  readonly filterModule = getModule(FilterModule, this.$store);
  protected memberModule = getModule(MemberModule, this.$store);

  pageTracker: PageTracker;
  htmlData = "";
  titleHasLongWord = false;
  topicLoaded = false;
  vccSupportArticle = VCCArticle;
  vccSupportSite = VCCSupportSite;
  feedbackResponded = false;

  spinnerShown = false;

  get activeTopicID(): string | number {
    return this.contentModule.activeTopic?.id || "";
  }

  get activeTopCatIndex(): number {
    const categories = this.contentModule.filteredCategories;
    for (let i = 0; i < categories.length; i++) {
      if (categories[i].id === this.contentModule.activeTopCategory.id) {
        return i;
      }
    }

    return -1;
  }

  get activeSubCatIndex(): number {
    const subCategories = this.contentModule.activeTopCategory.nodes;
    if (!subCategories) {
      return -1;
    }

    for (let i = 0; i < subCategories.length; i++) {
      if (subCategories[i].id === this.activeCategory.id) {
        return i;
      }
    }

    return -1;
  }

  get relatedTopics(): TopicModel[] {
    if (this.topicData.related) {
      return this.topicData.related.map((r) => this.contentModule.topicByID(r.id));
    }
    return [];
  }

  get ready(): boolean {
    return (
        this.filterModule.allFiltersSet &&
        this.contentModule.allDataLoaded &&
        this.contentModule.activeCategory !== undefined &&
        this.contentModule.activeTopic !== undefined
    );
  }

  get filteredCategories(): CategoryModel[] {
    return this.contentModule.filteredCategories;
  }

  get parentCategoryTitle(): string {
    return this.contentModule.activeCategory ? this.contentModule.activeCategory.title : "";
  }

  get currentMainCategory(): CategoryModel {
    return this.contentModule.activeTopCategory;
  }

  get currentSubCategory(): CategoryModel {
    return this.activeSubCatIndex >= 0 ? this.currentMainCategory.nodes[this.activeSubCatIndex] : undefined;
  }

  get activeCategory(): CategoryModel {
    return this.contentModule.activeCategory;
  }

  get nextTopic(): TopicModel | undefined {
    const categoryTopics = this.activeCategory.topics;

    // Currently on last topic for this category
    if (this.currentTopicIndex === categoryTopics.length - 1) {
      if (this.nextTopicCategory && this.nextTopicCategory.topics) {
        return this.nextTopicCategory.topics[0];
      }
      return undefined;
    }

    return categoryTopics[this.currentTopicIndex + 1];
  }

  get previousTopic(): TopicModel | undefined {
    // Currently on first topic for this category
    if (this.currentTopicIndex === 0) {
      if (this.previousTopicCategory && this.previousTopicCategory.topics) {
        return this.previousTopicCategory.topics[this.previousTopicCategory.topics.length - 1];
      }
      return undefined;
    }

    return this.activeCategory.topics[this.currentTopicIndex - 1];
  }

  get currentTopicIndex(): number {
    const categoryTopics = this.contentModule.activeCategory.topics;

    for (let i = 0; i < categoryTopics.length; i++) {
      if (categoryTopics[i].id === this.contentModule.activeTopic.id) {
        return i;
      }
    }
    return -1;
  }

  get activeTopCategory(): CategoryModel {
    return this.contentModule.activeTopCategory;
  }

  get topicLinkContainerClass(): string {
    let classes = [];

    if (this.topicLoaded) {
      classes.push("loaded");
    }
    if (!this.previousTopic || !this.nextTopic) {
      classes.push("single-link");
    }

    return classes.join(" ");
  }

  get topicData(): TopicModel {
    return this.contentModule.activeTopic;
  }

  getCategoryTitle(category: CategoryModel): string {
    return category ? category.title : "";
  }

  // Check if a given category is related to a different top category
  differentTopCategory(category: CategoryModel): boolean {
    if (!category) {
      return true;
    }

    const isActiveTopCategory = category.id === this.activeTopCategory?.id;
    const sameAsActiveCategory = category.id === this.activeCategory.id;

    // If the category is the same as the currently active category or top category
    if (isActiveTopCategory || sameAsActiveCategory) {
      return false;
    }

    const parentCategory = this.filteredCategories.find((c) => c.nodes && c.nodes.find((sc) => sc.id === category.id));
    return !parentCategory || parentCategory.id !== this.activeTopCategory?.id;
  }

  get previousTopicCategory(): CategoryModel | undefined {
    // If active category contains more topics before this
    if (this.currentTopicIndex > 0) {
      return this.activeCategory;
    }

    // Otherwise, find previous category with topics
    let categories = this.filteredCategories;
    let isFirstTopCategory = this.activeTopCatIndex === 0;

    // If current topic is located in sub category
    if (this.currentSubCategory) {
      const previousSubCategory = this.getPreviousSubCategory(this.currentMainCategory, this.activeSubCatIndex - 1);

      if (previousSubCategory) {
        return previousSubCategory;
      } else if (this.activeTopCategory.topics && this.activeTopCategory.topics.length > 0) {
        // if on the first subcategory and the top category contains topics
        return this.activeTopCategory;
      } else {
        if (isFirstTopCategory) {
          // No more categories
          return undefined;
        }

        // Return the next main category if it has topics or its first subcategory with topics
        return this.getMainOrSubCategory(categories[this.activeTopCatIndex - 1], false);
      }
    }

    if (isFirstTopCategory) {
      // No more categories
      return undefined;
    }

    // Return the next main category if it has topics or its first subcategory with topics
    return this.getMainOrSubCategory(categories[this.activeTopCatIndex - 1], false);
  }

  get nextTopicCategory(): CategoryModel | undefined {
    // If active category contains more topics after this
    if (this.currentTopicIndex < this.activeCategory.topics.length - 1) {
      return this.activeCategory;
    }

    // Otherwise find the next category
    let categories = this.filteredCategories;
    let isLastCategory = this.activeTopCatIndex === categories.length - 1;

    let nextSubCategory = this.getNextSubCategory(this.currentMainCategory, this.activeSubCatIndex + 1);
    if (nextSubCategory) {
      return nextSubCategory;
    }

    if (isLastCategory) {
      // No more categories
      return undefined;
    }

    // Return the next main category if it has topics or its first subcategory with topics
    return this.getMainOrSubCategory(categories[this.activeTopCatIndex + 1], true);
  }

  getMainOrSubCategory(category: CategoryModel, next: boolean): CategoryModel {
    if (next) {
      // Go to top category if it has topics
      if (category.topics && category.topics.length > 0) {
        return category;
      }
      return this.getNextSubCategory(category, 0);
    }

    // If the previous top category has sub categories, go to last sub cateogry
    if (category.nodes && category.nodes.length > 0) {
      return this.getPreviousSubCategory(category, (category.nodes?.length || 0) - 1);
    }

    // return the top category if it contains any topics
    return category.topics && category.topics.length > 0 ? category : undefined;
  }

  /**
   * Find the next sub category that contains any topics
   */
  getNextSubCategory(category: CategoryModel, startIndex: number): CategoryModel | undefined {
    const subCategories = category.nodes || [];

    if (startIndex >= subCategories.length) {
      return undefined;
    }

    for (let i = startIndex; i < subCategories.length; i++) {
      let result = subCategories[i];

      if (result.topics && result.topics.length > 0) {
        // result.topCategoryId = category.id;
        return result;
      }
    }

    return undefined;
  }

  /**
   * Find the previous sub category that contains any topics
   */
  getPreviousSubCategory(category: CategoryModel, startIndex: number): CategoryModel | undefined {
    const subCategories = category.nodes || [];

    if (startIndex < 0) {
      return undefined;
    }

    for (let i = startIndex; i >= 0; i--) {
      let result = subCategories[i];

      // Return the first sub category with topics
      if (result.topics && result.topics.length > 0) {
        return result;
      }
    }

    return undefined;
  }

  showImageZoomModal = false;
  imageZoomSrc!: string;
  imageZoomAlt!: string;
  imageZoomZoomable!: boolean;
  zoomScale!: number;

  onTopicContentClick(e: Event) {
    const $element = $(e.target);

    if (!($element.is("img") && $element.hasClass("clickable"))) {
      return;
    }

    const imgElement = $element as JQuery<HTMLImageElement>;

    let uri: any, url: string, img: any;

    const alt = imgElement.attr("alt");
    uri = new this.vccSupportSite.vccsupport.Uri($element.attr("src"));
    // uri.parameters.width = 800;
    if (imgElement.height() > imgElement.width()) {
      uri.parameters.height = uri.parameters.width;
      delete uri.parameters.width;
    }
    url = uri.toString(true);

    url = imgElement.data("src-full")
        ? new this.vccSupportSite.vccsupport.Uri(imgElement.data("src-full").toString(true))
        : url;
    
    const isSvg = url.toString().toLowerCase().indexOf('.svg') > -1;
    const imgWidth = imgElement[0].naturalWidth;
    
    // Svg target width set to max of window or 1400px to get good zoom
    const targetOriginalWidth = Math.max(window.outerWidth, 1400);

    this.zoomScale = isSvg ? (targetOriginalWidth / imgWidth) * 2 : 2;
    this.imageZoomZoomable = this.imageIsZoomable(imgElement);
    this.imageZoomSrc = url;
    this.imageZoomAlt = alt;
    this.showImageZoomModal = true;

    this.$nextTick(() => {
      this.showImageZoomModal = false;
    })
  }
  
  imageIsZoomable($element: JQuery<HTMLElement>): boolean {
    return $element.attr("zoomable")?.toLowerCase() === 'true' || $element.attr("data-zoomable")?.toLowerCase() === 'true' || false
  }

  clickableImages() {
    // Make (most) .figure-images clickable.
    if (
        $(
            ".article-content > .page_wide_image, .article-content > .two_columns, .article-content > .three_columns, .article-content > .special_texts"
        ).length === 0
    ) {
      $(".figure img[class!='symbol']").each((index: number, element: HTMLElement) => {
        const $element = $(element);
        if ($element.parents(".table").length < 1) {
          // Images inside tables shouldn't be clickable
          $element.addClass("clickable");
        }
      });
    }
  }

  initSpinners() {
    const spinners = $(".3d-spinner");

    spinners.each((index: number, element: HTMLElement) => {
      const spinner = $(element);

      if (spinner.is(":hidden")) {
        spinner.remove();
      } else {
        this.spinnerShown = true;
      }
    });

    if (spinners && spinners.length > 0) {
      $("head").prepend(`<link rel="stylesheet" type="text/css" href="/media/spinners/v60/main.css">`);
      $("footer").prepend(`<script async="" src="/media/spinners/v60/main.js" />`);
      // $('head').prepend(`<link rel="stylesheet" type="text/css" href="https://vcctripstorage.blob.core.windows.net/spinners/main.css">`)
      // $('footer').prepend(`<script async="" src="https://vcctripstorage.blob.core.windows.net/spinners/main.js" />`)
      return true;
    }

    // .each(function() {
    //   let spinnerId = $(this).data('spinner-id')

    // })
    return false;
  }

  addVideoThumbnails() {
    $(".multimedia").each((index: number, element: HTMLElement) => {
      const multiMedia = $(element);

      // Remove empty titles
      const videoTitle = $("h4", multiMedia);
      if (videoTitle.length && videoTitle.text().trim().length === 0) {
        videoTitle.remove();
      }

      const video = $("video", multiMedia);
      const videoThumbnail = $('<img class="video-thumbnail"></img>');
      videoThumbnail.attr("src", video.attr("poster") || "");
      multiMedia.append(videoThumbnail);
    });
  }

  addVideoEventListeners() {
    $("video").on("play", (e) => {
      const video = $(e.currentTarget);
      const src = video.attr("src") || "";
      const fileName = src
          .substring(src.lastIndexOf("/") + 1, src.lastIndexOf(".mp4"))
          .replace(/\s/g, "")
          .toLowerCase();
      this.$tracker.trackVideoPlayEvent(fileName);
    });

    // "ended"
    // $("video").on("pause ended", (e) => {
    //   const video = $(e.currentTarget);
    //   const src = video.attr("src") || "";
    //   const fileName = src
    //     .substring(src.lastIndexOf("/") + 1, src.lastIndexOf(".mp4"))
    //     .replace(/\s/g, "")
    //     .toLowerCase();
    // });
  }

  navigateBack() {
    const topCategory = this.activeTopCategory;
    const curCategory = this.activeCategory;

    const categoryPath = Helpers.urlFriendlyName(topCategory.title);

    if (curCategory.id !== topCategory.id) {
      this.$router.push({
        path: `${this.filterModule.basePath}/${categoryPath}`,
        hash: `#${Helpers.urlFriendlyName(curCategory.title)}`,
      });
    } else {
      this.$router.push({
        path: `${this.filterModule.basePath}/${categoryPath}`,
      });
    }
  }

  onPositiveFeedback() {
    this.$tracker.trackArticleFeedback("positive", window.location.href);
    this.feedbackResponded = true;
  }

  onNegativeFeedback() {
    this.$tracker.trackArticleFeedback("positive", window.location.href);
    this.feedbackResponded = true;

    // Force open feedback form
    setTimeout(() => {
      this.contentModule.setFeedbackFormOpen(true);
    }, 600);
  }

  topicLinkClick(category: CategoryModel, topic: TopicModel) {
    Helpers.scrollToTop();
    this.topicLoaded = false;

    setTimeout(() => {
      const topicUrlName = Helpers.urlFriendlyName(topic.title);
      this.$router.push({
        path: `${this.filterModule.basePath}/${Helpers.categoryBreadCrumb(
            category.id,
            this.filteredCategories
        )}${topicUrlName}/`,
      });
    }, 100);
  }

  async initiateData() {
    this.vccSupportSite = new VCCSupportSite();
    const activeTopic = this.contentModule.activeTopic;
    if (!activeTopic) {
      console.error("Topic missing");
      return;
    }

    const result = await Api.loadTopic(activeTopic.id, this.contentModule.locale, this.filterModule.platform, activeTopic.ref);

    const html = $(result);

    $("h1", html).attr("data-topic-id", activeTopic.id.toString());
    $("img", html).each((index, element) => {
      const $element = $(element);
      let src = $element.attr("src");

         $element.attr("data-src-full", src ? src.toString() : "");
      $element.attr("src", `${src}?width=${this.$config.defaultImageWidth}`);
      
      const zoomableImage = this.imageIsZoomable($element);
      if (zoomableImage) {
        $(`<span class="zoomable-icon"></span>`).insertBefore($element);
      }

      // show Kentucky ID
      const ktId = $element.attr("kt_id");
      if (ktId && ktId.length > 0) {
        $(`<span data-kt_id="${ktId}" class="kt_id">${ktId}</span>`).insertBefore($element);
      }
    });

    $(document).on("click", ".kt_id", (e) => {
      // Copy Kentucky id to clipboard
      const target = $(e.currentTarget);
      const ktId = target.data("kt_id");
      navigator.clipboard.writeText(ktId);
      target.html("&nbsp;&nbsp;&nbsp;Copied&nbsp;&nbsp;&nbsp;");

      setTimeout(() => {
        target.html(ktId);
      }, 2500)
    })

    this.htmlData = html[0].outerHTML;

    // Update page title
    const title = Helpers.setPageTitle(activeTopic.title);

    this.$tracker.trackPageView(title);

    // Check if title is a long word title
    const titleSplit = activeTopic ? activeTopic.title.split(" ") : [];
    let highestCharCount = 0;
    for (let i = 0; i < titleSplit.length; i++) {
      if (titleSplit[i].length > highestCharCount) {
        highestCharCount = titleSplit[i].length;
      }
    }
    if (highestCharCount > LONG_WORD_CHAR_COUNT) {
      this.titleHasLongWord = true;
    }

    setTimeout(() => {
      // Apply validity
      TopicHelper.applyContentValidity(
          $(".topic-article"),
          this.filterModule.activeFilters,
          this.$config,
          this.$validityHelper
      );
      // Run script

      this.vccSupportArticle = new VCCArticle(this.vccSupportSite);
      const gearboxFilter = this.filterModule.activeFilters.find(
          (f) => f.domain === ValidityDomains.gearboxes
      );
      const structureWeekFilter = this.filterModule.activeFilters.find(
          (f) => f.domain === ValidityDomains.structureWeek
      );
      TopicHelper.addGearboxHighlight(gearboxFilter as VariantFilter<string[]>, this.$t);
      TopicHelper.highlightNews(structureWeekFilter as VariantFilter<string>);

      this.topicLoaded = true;

      this.clickableImages();
      Helpers.loadingComplete();

      Helpers.scrollToTop();
      // Wait for images to load and then scroll to top
      $(".topic-article").imagesLoaded(() => {
        this.$nextTick(() => {
          this.initSpinners();
        });

        this.addVideoThumbnails();
        this.addVideoEventListeners();
        Helpers.scrollToTop();

        this.$emit("pageReady");
      });
    }, 100);

    setTimeout(() => {
      const startOffset = parseInt($("body").css("padding-top"));
      this.pageTracker.init($(".page-content-inner")[0], startOffset);
    }, 1000)
  }

  async initiate() {
    Helpers.loadingStart();
    if (this.ready) {
      await this.initiateData();
    }
  }

  @Watch("activeTopicID")
  onActiveTopicIdChange() {
    this.$nextTick(async () => {
      await this.initiateData();
    });
  }

  updated() {
    if (this.spinnerShown) {
      this.spinnerShown = false;
    }
  }

  beforeRouteLeave(to: Route, from: Route, next: () => any) {
    this.sendPageTrack();
    next();
  }

  beforeRouteUpdate(to: Route, from: Route, next: () => any) {
    this.sendPageTrack();
    next();
  }

  onPageUnload() {
    if (this.contentModule.activeTopic) {
      this.sendPageTrack();
    }
  }

  protected sendPageTrack() {
    const topic = this.contentModule.activeTopic;
    const category = this.contentModule.activeTopCategory;
    const subCategory = this.contentModule.activeSubCategory;

    const duration = this.pageTracker.getDuration();
    const progress = this.pageTracker.getProgress();

    this.$tracker.trackPage(
        topic,
        this.filterModule.basePath,
        this.contentModule.locale,
        duration,
        progress,
        null,
        category,
        subCategory
    );
  }

  async created() {
    this.pageTracker = new PageTracker();
    await this.initiate();
    this.$nextTick(() => {
      Helpers.scrollToTop({animTime: 0});
    });
  }

  destroyed() {
    window.removeEventListener("beforeunload", this.onPageUnload.bind(this));
    this.pageTracker.destroy();
  }
}
